import { useCallback, useMemo, useState } from "react";
import { z } from "zod";
import {
  ALL_GROUP_KEY,
  Box,
  DataTable,
  DataTableCell,
  DataTableCellContent,
  DENSITY_CONFIG,
  Icon,
  Menu,
  MenuPopup,
  MenuRadioGroup,
  MenuRadioItem,
  MenuSub,
  MenuSubPopup,
  MenuSubTrigger,
  MenuTrigger,
  Text,
  WidgetDisclosureRow,
  WidgetEmpty,
  WidgetSkeleton,
  useWidgetSurface,
  type DataTableColumnDef,
  type DataTableGroupType,
  type IconName,
  type WidgetModule,
  type WidgetRenderContext,
  type WidgetSettingsGroup,
  WidgetListHeader,
  type WidgetListColumn,
  useElementSize,
  WidgetBleedScroller,
  widgetListColumnsFor,
} from "@salestrak/ui";
import { EntityTag } from "#app/components/entity";
import { FilterToggle } from "#app/components/filters/filter-toggle";
import { IssueGroupByChip } from "#app/components/issues/issue-group-by-chip";
import { IssueGroupTitle } from "#app/components/issues/issue-group-title";
import type { IssueTableGroupBy } from "#app/components/issues/issue-grouping";
import { GroupQueryProvider, useGroupStates } from "#app/lib/hooks/group-query-provider";
import { useIssueQueryGroups } from "#app/hooks/issue-list/use-issue-query-groups";
import { useIssueView } from "#app/components/issue-view";
import { type IssueRow, IssuesDocument } from "#app/lib/data";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import {
  ENTITY_KIND_ICON,
  resolveEntityLabel,
  type EntityData,
} from "#app/lib/entity-visuals";
import { DateText } from "#app/components/date-text";
import { pluralize } from "#app/lib/utils/pluralize";
import { useWindowEmptyTitle } from "#app/widgets/dashboards/scope/use-window-copy";
import type { MemberBreakdown } from "./use-team-member-insights";
import { useTeamMemberInsights } from "./use-team-member-insights";
import { useMemberProjects } from "./use-member-projects";

/**
 * `team-members` — a managed-only, team-scoped roster and work snapshot. Every
 * collapsed row shows real activity from the dashboard period; one row opens
 * inline into a line of facts, the table's own view controls, and project
 * links. The expanded table is the member's OWN windowed paged query (one per
 * group while grouped) — it reaches every assigned project, and its total
 * matches the stated "assigned projects" count by construction (both read the
 * same server aggregation/predicate).
 *
 * OPTIONS SIT AT TWO LEVELS, and the split is what each one PERSISTS. The
 * card's "…" holds what the widget IS for everyone who sees the board (the
 * roster order). The panel's chips hold how ONE viewer is reading ONE member's
 * list right now, and nothing there is saved. So the panel gets exactly one
 * chip per question, in the trailing corner the card puts its own controls in —
 * two levels, one visual language, and neither one a toolbar band of its own.
 *
 * The panel's facts are what the roster row above does not carry: the row states
 * the member's activity (projects updated, updates), the panel their coverage
 * (assigned projects, and the spread across workflows and types). Repeating
 * either figure 40px from itself reads as a stutter, so each fact is stated
 * once.
 *
 * They are a LINE OF TEXT, not headline figures. `StatBand` is the right shape
 * where a row of figures IS the card — the stat strip, the outcome summary —
 * and it earns its height there by filling grid rows the card claims anyway.
 * Here the TABLE is the subject and the figures annotate it, so tiles spend
 * ~36px stating three facts at twice the size of the rows they describe, and
 * three inert numbers sitting where every other band on the board puts a press
 * target invite a click with nowhere to go: the rows such a column would
 * disclose are already open beneath it.
 */
export const teamMembersConfigSchema = z
  .looseObject({
    leadsFirst: z.boolean().default(true),
  });

export type TeamMembersConfig = z.infer<typeof teamMembersConfigSchema>;

/** Newest-touched first — module-level so the paged query variables stay stable. */
const PROJECT_ORDER = { lastUpdateDate: "desc" } as const;

const getIssuesConnection = (data: any) => data?.issues;

/** The table's trailing gap after each group — its own "md" group gap. */
const GROUP_GAP = 16;

/** The "no value picked" radio, since a radio group needs a value to hold. */
const ANY_VALUE = "__any__";

function MemberFilterRow({
  icon,
  label,
  allLabel,
  options,
  selectedId,
  onSelect,
}: {
  icon: IconName;
  label: string;
  allLabel: string;
  options: MemberBreakdown[];
  selectedId: string | null;
  onSelect: (id: string | null) => void;
}) {
  const selected = options.find((option) => option.id === selectedId);
  return (
    <MenuSub>
      <MenuSubTrigger>
        <Icon name={icon} size="sm" color="muted" />
        <Text size="sm">{label}</Text>
        {/* The readout TAKES the row's free space and ends against the
            chevron's own auto margin — a second margin here would split the gap
            and leave every value floating mid-row. Same row shape as the
            toolbar's quick filters. */}
        <Box direction="row" align="center" justify="end" fill="flex" minWidth={0}>
          <Text size="sm" variant="muted" truncate={1}>
            {selected?.label ?? allLabel}
          </Text>
        </Box>
      </MenuSubTrigger>
      <MenuSubPopup autoHighlight>
        <MenuRadioGroup
          value={selectedId ?? ANY_VALUE}
          onValueChange={(value) =>
            onSelect(value === ANY_VALUE ? null : String(value))
          }
        >
          <MenuRadioItem value={ANY_VALUE}>{allLabel}</MenuRadioItem>
          {options.map((option) => (
            <MenuRadioItem key={option.id} value={option.id}>
              <Box direction="row" align="center" justify="between" gap="sm" fill="width">
                <Text size="sm" truncate={1}>
                  {option.label}
                </Text>
                {/* The bucket's own size — the same aggregation the band
                    counts, so the pick is made knowing what it leaves. */}
                <Text size="xs" variant="muted" tabular>
                  {option.count.toLocaleString()}
                </Text>
              </Box>
            </MenuRadioItem>
          ))}
        </MenuRadioGroup>
      </MenuSubPopup>
    </MenuSub>
  );
}

/**
 * The panel's filters, as ONE chip over one hover menu — the shape the toolbar's
 * Simple filters settled on. Two chips that each opened a list of values put two
 * chip species in one row (the group-by chip beside them is the app's own
 * `FilterToggle`) and gave neither a way to clear without opening it. One chip
 * states the count, owns the single clear, and its rows state their values, so
 * the applied set is legible without opening anything.
 */
function MemberProjectFilters({
  workflows,
  projectTypes,
  workflowId,
  projectTypeId,
  onWorkflowChange,
  onProjectTypeChange,
}: {
  workflows: MemberBreakdown[];
  projectTypes: MemberBreakdown[];
  workflowId: string | null;
  projectTypeId: string | null;
  onWorkflowChange: (id: string | null) => void;
  onProjectTypeChange: (id: string | null) => void;
}) {
  const activeCount = (workflowId ? 1 : 0) + (projectTypeId ? 1 : 0);
  return (
    <Menu>
      <MenuTrigger
        render={
          <FilterToggle
            size="xs"
            iconSize="xs"
            icon="filter"
            count={activeCount}
            onClear={() => {
              onWorkflowChange(null);
              onProjectTypeChange(null);
            }}
            tooltip="Narrow this member's Projects"
            clearTooltip="Clear all filters"
          />
        }
      />
      {/* `panel` rung, so a row's `icon label … value ▸` has room to state its
          value rather than truncating it. No `autoHighlight` on the parent: the
          chip was pressed, so no row is the one a press would act on yet. */}
      <MenuPopup align="end" width="panel">
        <MemberFilterRow
          icon={ENTITY_KIND_ICON.workflow}
          label="Workflow"
          allLabel="All workflows"
          options={workflows}
          selectedId={workflowId}
          onSelect={onWorkflowChange}
        />
        <MemberFilterRow
          icon={ENTITY_KIND_ICON.issueType}
          label="Project type"
          allLabel="All Project types"
          options={projectTypes}
          selectedId={projectTypeId}
          onSelect={onProjectTypeChange}
        />
      </MenuPopup>
    </Menu>
  );
}

function ExpandedMember({
  userId,
  wide,
  maximized,
  updatesByMemberProject,
}: {
  userId: string;
  wide: boolean;
  /** The card is the maximized preview, which pads its own body symmetrically. */
  maximized: boolean;
  /** Per-(member, project) event counts keyed `"<userId>:<issueId>"`. */
  updatesByMemberProject: Record<string, number>;
}) {
  const { openIssue } = useIssueView();
  // The surface the enclosing widget card painted — the inner table's opaque
  // parts must blend with it.
  const widgetSurface = useWidgetSurface();
  const [workflowId, setWorkflowId] = useState<string | null>(null);
  const [projectTypeId, setProjectTypeId] = useState<string | null>(null);
  // Grouping is a per-viewer view choice on this panel — plain component
  // state, never persisted into the widget config.
  const [groupBy, setGroupBy] = useState<IssueTableGroupBy>("none");
  // Exact per-member aggregations (the band + the filter menus) and the folded
  // `where` the paged table reads — nothing here derives from a capped sample.
  const { assignedTotal, workflows, projectTypes, tableWhere, error } = useMemberProjects(
    userId,
    workflowId,
    projectTypeId,
  );
  const groupDimension = groupBy === "none" ? null : groupBy;
  const {
    groups: queryGroups,
    ready: groupsReady,
    error: groupsError,
  } = useIssueQueryGroups({ where: tableWhere, dimension: groupDimension });
  const { workflowMap } = useWorkspaceData();
  // Names the dashboard window the rows were fetched on, so an empty panel
  // points at the date control rather than at nothing.
  const emptyTitle = useWindowEmptyTitle("No assigned Projects");
  const panelError = error ?? groupsError;
  if (panelError) throw panelError;


  const columns = useMemo<DataTableColumnDef<IssueRow>[]>(() => {
    const result: DataTableColumnDef<IssueRow>[] = [
      {
        id: "name",
        header: "Project",
        accessorFn: (issue) => issue.name,
        meta: { minWidth: 180, width: 280 },
        cell: ({ row }) => (
          <DataTableCellContent>
            <EntityTag
              entity={{
                kind: "issue",
                id: row.original.issueId,
                issue: {
                  name: row.original.name,
                  icon: row.original.icon,
                },
                workflowId: row.original.workflowId,
              }}
              display="label"
              weight="regular"
              interactive={false}
            />
          </DataTableCellContent>
        ),
      },
    ];
    if (wide) {
      result.push(
        {
          id: "workflow",
          header: "Workflow",
          accessorFn: (issue) => workflowMap.get(issue.workflowId)?.name ?? "Unknown workflow",
          meta: { minWidth: 140, maxWidth: 200 },
          cell: ({ row }) => (
            <DataTableCell>
              {workflowMap.get(row.original.workflowId)?.name ?? "Unknown workflow"}
            </DataTableCell>
          ),
        },
        {
          id: "type",
          header: "Project type",
          accessorFn: (issue) => issue.issueType?.name ?? "No Project type",
          meta: { minWidth: 130, maxWidth: 180 },
          cell: ({ row }) => (
            <DataTableCell>{row.original.issueType?.name ?? "No Project type"}</DataTableCell>
          ),
        },
      );
    }
    result.push({
      id: "activity",
      header: "Recent activity",
      accessorFn: (issue) => updatesByMemberProject[`${userId}:${issue.id}`] ?? 0,
      meta: { minWidth: 150, maxWidth: 190 },
      cell: ({ row }) => {
        const recentUpdates = updatesByMemberProject[`${userId}:${row.original.id}`] ?? 0;
        const activity =
          recentUpdates === 0
            ? "No recent updates"
            : `${recentUpdates} ${recentUpdates === 1 ? "update" : "updates"}`;
        const updated = row.original.lastUpdateDate;
        return (
          <DataTableCell textBrightness={recentUpdates === 0 ? "subtle" : "high"}>
            {activity}
            {updated ? (
              <>
                {" · "}
                <DateText date={updated} format="compact" variant="default" truncate={false} />
              </>
            ) : null}
          </DataTableCell>
        );
      },
    });
    return result;
  }, [wide, workflowMap, updatesByMemberProject, userId]);

  // The member's projects page through their OWN windowed server query (one
  // per group while grouped) — the DataTable's virtual scroll reaches every
  // assigned row, never a slice.
  const { states, registerState, getGroupProps } = useGroupStates<IssueRow>();
  const projectGroups = useMemo<DataTableGroupType<IssueRow>[]>(() => {
    if (groupDimension) {
      return (queryGroups ?? []).map((def) => ({
        key: def.key,
        label: def.label,
        totalCount: states.get(def.key)?.total ?? def.totalCount,
        ...getGroupProps(def.key),
      }));
    }
    return [
      {
        key: ALL_GROUP_KEY,
        label: "Projects",
        totalCount: states.get(ALL_GROUP_KEY)?.total,
        ...getGroupProps(ALL_GROUP_KEY),
      },
    ];
  }, [groupDimension, queryGroups, states, getGroupProps]);
  const tableTotal = groupDimension
    ? (queryGroups ?? []).reduce(
        (sum, def) => sum + (states.get(def.key)?.total ?? def.totalCount),
        0,
      )
    : (states.get(ALL_GROUP_KEY)?.total ?? 0);
  const tableLoading = groupDimension
    ? !groupsReady
    : (states.get(ALL_GROUP_KEY)?.loading ?? true);
  const tableEmpty = !tableLoading && tableTotal === 0;
  const filtered = workflowId !== null || projectTypeId !== null;

  // The panel's one line of facts. Unfiltered it states the coverage — how much
  // work, spread over how many workflows and types. Under a filter the spread
  // is no longer the subject (the chip names what is picked, and the menu's own
  // rows carry each bucket's size), so the line states the narrowing instead:
  // the table's count against the whole. `assignedTotal` ignores the panel's
  // filters by construction, which is exactly why the "of" clause is needed —
  // a bare 34 over 12 rows reads as a broken count.
  //
  // A member with no assigned work states nothing here at all: the table's own
  // empty line already says it, and three zeros over "No assigned projects" is
  // the same absence printed twice.
  // Nothing assigned means nothing to group and nothing to narrow: both menus
  // would open onto empty pools, which is the dead end the toolbar's own quick
  // filters hide rather than offer. The panel then carries its empty line alone.
  const hasControls = assignedTotal > 0 || filtered || groupDimension !== null;

  const facts = !assignedTotal
    ? null
    : filtered
      ? `${tableLoading ? "…" : tableTotal.toLocaleString()} of ${assignedTotal.toLocaleString()} assigned ${pluralize("Project", { count: assignedTotal })}`
      : [
          `${assignedTotal.toLocaleString()} assigned ${pluralize("Project", { count: assignedTotal })}`,
          `${workflows.length} ${pluralize("workflow", { count: workflows.length })}`,
          // The narrow card drops the last fact rather than truncating the line
          // — same width call its table makes when it drops these two columns.
          ...(wide
            ? [`${projectTypes.length} Project ${pluralize("type", { count: projectTypes.length })}`]
            : []),
        ].join(" · ");

  const groupTitleDimension =
    groupDimension && groupDimension !== "age" ? groupDimension : null;
  const renderGroupTitle = useCallback(
    (groupKey: string) =>
      groupTitleDimension ? (
        <IssueGroupTitle dimension={groupTitleDimension} groupKey={groupKey} />
      ) : (
        groupKey
      ),
    [groupTitleDimension],
  );

  // Internal scroll needs a bounded viewport: fit up to 8 rows exactly, then
  // clamp with a half-row peek so longer lists read as scrollable. The column
  // header is ONE band at either grouping (`sharedHeader` hoists it above the
  // first group); grouped, each group adds its own band plus the table's
  // trailing group gap, and a hard ceiling keeps a many-group dimension from
  // stretching the panel.
  const rowHeight = DENSITY_CONFIG.compact.rowHeight;
  const headerHeight = DENSITY_CONFIG.compact.headerHeight;
  const dataRowsShown = Math.min(Math.max(tableTotal, 1), 8.5);
  const tableHeight =
    headerHeight +
    (groupDimension
      ? Math.min(
          Math.max(projectGroups.length, 1) * (headerHeight + GROUP_GAP) +
            dataRowsShown * rowHeight,
          560,
        )
      : dataRowsShown * rowHeight);

  return (
    <Box
      marginTop="sm"
      // The card's autoHeight body ends on an `xs` inset — enough under a line
      // of text, too tight under a bordered island, whose gutter then reads
      // uneven against the body's own sides. The maximized preview already pads
      // symmetrically.
      marginBottom={maximized ? undefined : "base"}
      direction="column"
      rounded="md"
      border
      overflow="hidden"
      surface="card"
    >
      {/* The facts and the panel's controls share ONE row: the line states what
          the table is, the chips hold the trailing corner where the card puts
          its own controls. The row draws no bottom rule — the table's column
          header draws the first one, and a second hairline 36px above it is the
          double border `entity-list`'s own in-body toolbar also declines. */}
      {hasControls ? (
        <Box
          direction="row"
          align="center"
          gap="sm"
          wrap
          paddingX="md"
          paddingY="xs"
          minHeight={40}
        >
          {facts ? (
            <Box fill="flex" minWidth={120}>
              <Text size="xs" variant="muted" truncate={1}>
                {facts}
              </Text>
            </Box>
          ) : null}
          {/* `marginLeft="auto"` rather than the row's `justify`: space-between
              distributes per LINE, so a wrapped cluster would sit at the left of
              its own line. An auto margin holds the trailing edge on both — and
              holds it when there are no facts beside the chips at all. */}
          <Box
            direction="row"
            align="center"
            gap="xs"
            shrink={false}
            marginLeft="auto"
          >
            {/* Both chips ghost at rest and the primary-hued outline once they hold
                something — the treatment every FilterToggle in the app wears, and
                what `entity-list`'s own in-body toolbar pairs. A resting border on
                one of two neighbours reads as two kinds of control. */}
            <IssueGroupByChip groupBy={groupBy} onGroupByChange={setGroupBy} />
            <MemberProjectFilters
              workflows={workflows}
              projectTypes={projectTypes}
              workflowId={workflowId}
              projectTypeId={projectTypeId}
              onWorkflowChange={setWorkflowId}
              onProjectTypeChange={setProjectTypeId}
            />
          </Box>
        </Box>
      ) : null}

      {groupDimension ? (
        (queryGroups ?? []).map((def) => (
          <GroupQueryProvider<IssueRow>
            key={def.key}
            groupKey={def.key}
            document={IssuesDocument}
            variables={{ where: def.where, orderBy: PROJECT_ORDER, limit: 50 }}
            getConnection={getIssuesConnection}
            onStateChange={registerState}
          />
        ))
      ) : (
        <GroupQueryProvider<IssueRow>
          groupKey={ALL_GROUP_KEY}
          document={IssuesDocument}
          variables={{ where: tableWhere, orderBy: PROJECT_ORDER, limit: 50 }}
          getConnection={getIssuesConnection}
          onStateChange={registerState}
        />
      )}
      {tableEmpty ? (
        <Box padding="md" align="center" border="top">
          <Text size="sm" variant="muted">
            {/* Only a NARROWED table has filters to blame. Unnarrowed, the
                absence is the member's own and the window is the thing worth
                naming, so the reader's next move is the date control. */}
            {filtered ? "No Projects match these filters." : emptyTitle}
          </Text>
        </Box>
      ) : (
        <Box direction="column" height={tableHeight}>
          <DataTable
            columns={columns}
            groups={projectGroups}
            getRowId={(issue) => issue.issueId}
            groupTitle={groupTitleDimension ? renderGroupTitle : undefined}
            loading={tableLoading}
            surface={widgetSurface}
            density="compact"
            // ONE column header for the whole table, group bands under it, and
            // it stays put while the panel's own viewport scrolls — grouped,
            // a header per group repeated the same four words down the panel.
            sharedHeader
            stickyHeader
            paddingX="md"
            paddingXAlign="content"
            onRowClick={(row) => openIssue(row.original as unknown as IssueRow, { source: "widget" })}
          />
        </Box>
      )}
    </Box>
  );
}

/**
 * The roster row's figure columns, named once in the list header: Projects
 * updated always; Updates joins from 420px of body. Measured, never span.
 */
const TEAM_MEMBER_COLUMNS = [
  { key: "projects", label: "Projects updated", width: 104, minWidth: 0 },
  { key: "updates", label: "Updates", width: 64, minWidth: 420 },
] as const satisfies readonly WidgetListColumn[];

function TeamMembersRender({
  w,
  // The card's maximized preview. Named apart from the expanded ROW — one is
  // the card's own presentation, the other is which member is open.
  expanded: maximized = false,
  config,
}: WidgetRenderContext<TeamMembersConfig>) {
  const { isTeamScope, insights, updatesByMemberProject, loading } = useTeamMemberInsights(
    config.leadsFirst,
  );
  // Columns follow the MEASURED body width, never the grid span.
  const { ref: bodyRef, size: bodySize } = useElementSize<HTMLDivElement>();
  const columns = widgetListColumnsFor(TEAM_MEMBER_COLUMNS, bodySize.width);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const wide = w >= 6;

  if (!isTeamScope) {
    return (
      <WidgetEmpty
        title="Scope to a team"
      />
    );
  }
  if (loading) {
    // The header's labels are static copy, so it stands real over anonymous
    // rows on the roster's rung; the body measures here too, so the figure
    // bars sit under the columns the width keeps.
    return (
      <Box ref={bodyRef} direction="column" grow minHeight={0}>
        <WidgetListHeader identity="Member" columns={columns} caretSlot />
        <WidgetSkeleton
          variant="list"
          leading="circle"
          caretSlot
          figures={columns.map((column) => column.width)}
        />
      </Box>
    );
  }
  if (insights.length === 0) {
    return (
      <WidgetEmpty
        title="No members yet"
      />
    );
  }

  return (
    // The card's height is the board's: the roster scrolls inside it, each
    // member's disclosed Projects table riding along in the same scroller.
    // The scroller bleeds `sm` and the content pads back in, so the rows'
    // hover wash (a negative margin) never becomes horizontal overflow.
    <Box ref={bodyRef} direction="column" grow minHeight={0}>
      <WidgetListHeader identity="Member" columns={columns} caretSlot />
    <WidgetBleedScroller inset="sm">
  {insights.map((insight) => {
    const expanded = expandedId === insight.member.userId;
    // One entity for the chip AND the row's accessible name, so the button
    // is named with the same label the reader sees on it.
    const memberEntity: EntityData = {
      kind: "user",
      id: insight.member.userId,
      user: insight.member,
    };
    return (
      <Box key={insight.member.userId}>
        <WidgetDisclosureRow
          expanded={expanded}
          label={resolveEntityLabel(memberEntity)}
          onToggle={() =>
            setExpandedId((current) =>
              current === insight.member.userId ? null : insight.member.userId,
            )
          }
        >
          <Box fill="flex" shrink minWidth={0}>
            <EntityTag
              entity={memberEntity}
              subtitle={wide}
              weight={expanded ? "semibold" : "medium"}
              interactive={false}
            />
          </Box>
          {columns.map((column) => (
            <Box key={column.key} width={column.width} shrink={false} align="end">
              <Text size="sm" weight="semibold" tabular>
                {column.key === "projects" ? insight.updatedProjects : insight.recentUpdates}
              </Text>
            </Box>
          ))}
        </WidgetDisclosureRow>
        {expanded ? (
          <ExpandedMember
            userId={insight.member.userId}
            wide={wide}
            maximized={maximized}
            updatesByMemberProject={updatesByMemberProject}
          />
        ) : null}
      </Box>
    );
  })}
    </WidgetBleedScroller>
    </Box>
  );
}

const teamMembersSettings: WidgetSettingsGroup[] = [
  {
    title: "Display",
    fields: [
      {
        key: "leadsFirst",
        kind: "toggle",
        label: "Show leads first",
        icon: "award",
      },
    ],
  },
];

export const teamMembersDef: WidgetModule<TeamMembersConfig> = {
  type: "team-members",
  title: "Team members",
  icon: "users",
  span: { minW: 2, minH: 3, maxW: 12, maxH: 12, defaultW: 4 },
  // The roster owns its expansion (each row discloses in place); the detail
  // dialog would show the same list again.
  chromeActions: { maximize: false },
  // `body: "bleed-bottom"`, never `"bleed"`: the rows are `WidgetDisclosureRow`s,
  // whose hover wash bleeds past the content edge on its own — a body with no
  // horizontal padding would land that wash on the card's border, where the
  // grid clips it. The roster owns its scrollport under the sticky column
  // header, so it drops the card's END inset alone: its scroller reaches the
  // card's edge and the `xl` gutter inside it is the slack, instead of the
  // card's inset standing as a dead band under the fade.
  body: "bleed-bottom",
  configSchema: teamMembersConfigSchema,
  settings: teamMembersSettings,
  render: TeamMembersRender,
};
